import { Button } from '@mui/material';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { CheckoutErrorBanner } from '@components/ProductOffers/modals/ShoppingCartDrawer/CheckoutErrorBanner';
import LoadingButton from '@shared/components/buttons/LoadingButton/LoadingButton';
import type { CartSummary } from '@hooks/useShoppingCart/useShoppingCart';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';

interface CartFooterProps {
  onShowCart: () => void;
  onCheckout: () => void;
  checkoutError?: boolean;
  isCheckoutPending?: boolean;
  onOpenIntakeForm?: () => void;
  cartSummary: CartSummary;
}

const fmt = (amount: number): string => `$${amount.toFixed(2)}`;

// Compact mobile money format: drop a trailing ".00" so whole amounts read as
// "$0" / "$50" while fractional amounts keep both decimals ("$50.50"). Isolated
// here so the compact rule is a one-line change independent of desktop `fmt`.
const fmtCompact = (amount: number): string => fmt(amount).replace(/\.00$/, '');

export const CartFooter = ({
  onShowCart,
  onCheckout,
  checkoutError,
  isCheckoutPending,
  onOpenIntakeForm,
  cartSummary,
}: CartFooterProps) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const { monthlyTotal, oneTimeTotal, itemCount } = cartSummary;

  const hasItems = itemCount > 0;

  const showCartButton = (
    <Button
      variant="footerAction"
      onClick={onShowCart}
      startIcon={<ShoppingCartOutlinedIcon />}
    >
      Show Cart
    </Button>
  );

  const checkoutButton = (
    <LoadingButton
      variant="contained"
      disabled={!hasItems}
      loading={isCheckoutPending}
      onClick={onCheckout}
    >
      Checkout
    </LoadingButton>
  );

  return (
    <Stack sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }}>
      {checkoutError && onOpenIntakeForm && <CheckoutErrorBanner onOpenIntakeForm={onOpenIntakeForm} />}
      <Paper variant="cartFooterBar">
        <Container>
          {isCompact ? (
            // Mobile: totals and the two action buttons share one row when there is
            // room (buttons side by side, like the equipment/add-ons dialog footer).
            // flexWrap lets the button group drop to its own line — still side by side,
            // right-aligned via ml:auto — when the row is too narrow to fit everything.
            <Stack direction="row" alignItems="center" spacing="12px" useFlexGap sx={{ width: '100%', flexWrap: 'wrap' }}>
              <Stack direction="row" alignItems="center" spacing="20px" useFlexGap>
                <Stack alignItems="flex-start">
                  <Typography variant="price.total">{fmtCompact(monthlyTotal)}</Typography>
                  <Typography variant="subtitle.card">Month</Typography>
                </Stack>
                {oneTimeTotal > 0 && (
                  <Stack alignItems="flex-start">
                    <Typography variant="price.total">{fmtCompact(oneTimeTotal)}</Typography>
                    <Typography variant="subtitle.card">One-Time</Typography>
                  </Stack>
                )}
                <Stack alignItems="flex-start">
                  <Typography variant="price.total">{itemCount}</Typography>
                  <Typography variant="subtitle.card">Items</Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing="10px" useFlexGap sx={{ ml: 'auto' }}>
                {showCartButton}
                {checkoutButton}
              </Stack>
            </Stack>
          ) : (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              columnGap={2}
            >
              <Stack direction="row" alignItems="center" spacing="30px">
                <Typography variant="label.field" mr="20px">
                  Cart Total:
                </Typography>
                <Typography variant="price.total" mr="30px">
                  {fmt(monthlyTotal)} / Month
                </Typography>
                {oneTimeTotal > 0 && (
                  <>
                    <Typography variant="label.field" mr="20px">
                      One-Time:
                    </Typography>
                    <Typography variant="price.total" mr="30px">
                      {fmt(oneTimeTotal)}
                    </Typography>
                  </>
                )}
                <Typography variant="label.field" mr="20px">
                  Item in a Cart:
                </Typography>
                <Typography variant="price.total" mr="30px">
                  {itemCount}
                </Typography>
                {showCartButton}
              </Stack>

              <Stack direction="row" alignItems="center" spacing="16px" flexShrink={0}>
                {checkoutButton}
              </Stack>
            </Stack>
          )}
        </Container>
      </Paper>
    </Stack>
  );
};
